<template>
    <div class="ecomm-setup-page">
        <h2>{{ $options.TITLE }}</h2>
        <div class="actions">
            <div class="search-bar">
                <ds-guide
                    :show="displaySearchGuide"
                    position="bottom"
                    :body-text="$options.SEARCH_GUIDE_TEXT"
                    confirm-text="Ok, got it"
                    has-close
                    @confirm="dismissSearchGuide"
                    @cancel="dismissSearchGuide"
                >
                    <template #reference>
                        <ds-input-field
                            v-model="searchInput"
                            :label="$options.SEARCH_LABEL"
                            @focus="dismissSearchGuide"
                        >
                            <ds-icon slot="leading" name="search" />
                        </ds-input-field>
                    </template>
                </ds-guide>
            </div>

            <div class="setup-guide" />

            <ds-guide
                :show="displayQuickActionsGuide"
                position="bottom"
                :body-text="$options.QUICK_ACTIONS_GUIDE_TEXT"
                confirm-text="Ok, got it"
                has-close
                @confirm="dismissQuickActionsGuide"
                @cancel="dismissQuickActionsGuide"
            >
                <template #reference>
                    <ecomm-quick-actions
                        :checkout-page-link="checkoutPageLink"
                        :hide-convrrt-checkout="hideConvrrtCheckout"
                        :label="$options.QUICK_ACTIONS_LABEL"
                        class="quick-actions"
                        @focus="dismissQuickActionsGuide"
                    />
                </template>
            </ds-guide>
        </div>

        <ds-modal :is-open="showPaypalVaultingModal" dialog>
            <h4 class="paypal-vaulting-title">
                {{ $t('paypal.vaulting.modal.title') }}
            </h4>
            <p> {{ $t('paypal.vaulting.modal.description') }} </p>
            <ds-spinner v-if="isVaultingActionInProgress" class="centered-spinner" />
            <div v-else>
                <ds-text-button destructive @click="disconnectPaypal">
                    {{ $t('global.disconnect') }}
                </ds-text-button>
                <ds-outline-button as="a" :href="paypalOnboardingUrl" @click="reconnectPaypal">
                    {{ $t('global.reauthenticate') }}
                </ds-outline-button>
            </div>
        </ds-modal>

        <ecomm-settings-block
            v-for="block in filteredSettingsBlocks"
            :key="block.title"
            :block-configuration="block"
            :checkout-page-link="checkoutPageLink"
            :default-theme="defaultTheme"
            :secure-server-string="secureServerString"
            :display-guide="displayLearnMoreGuide"
            :guide-text="$options.LEARN_MORE_GUIDE_TEXT"
            :is-keap-pay-enabled="isKeapPayEnabled"
            :is-ultimate="isUltimate"
            :is-manage-merchant-account="isManageMerchantAccounts"
            @dismissGuide="dismissLearnMoreGuide"
        />

        <div v-if="filteredSettingsBlocks.length === 0" class="no-result">
            <h3>{{ $options.NO_RESULTS }}</h3>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import EcommQuickActions from './EcommQuickActions.vue';
import EcommSettingsBlock from './EcommSettingsBlock.vue';
import ECOMM_SETUP_ENTRIES from './ecommSetupEntries.json';
import { loadFtu, updateFtuItem } from '@/ftu.utils';
import { FTU_TYPES } from '@/ftu.constants';

export const TITLE = 'E-Commerce';
export const SEARCH_LABEL = 'Search E-Commerce';
export const QUICK_ACTIONS_LABEL = 'Quick actions';
export const NO_RESULTS = 'No results';
export const GUIDE_TIMEOUT_MS = 1000;
export const INITIAL_GUIDE_TIMEOUT_MS = 3000;
export const QUICK_ACTIONS_GUIDE_TEXT = 'Click here to access  common actions like "Enter a payment" or "Create a product"';
export const SEARCH_GUIDE_TEXT = 'Too many things? Click here to search for what you’re looking for.';
export const LEARN_MORE_GUIDE_TEXT = 'Need help understanding how it all works? Click these links at the bottom of each card to learn more about each feature.';

export default {
    ecommSetupEntries: ECOMM_SETUP_ENTRIES,
    TITLE,
    SEARCH_LABEL,
    QUICK_ACTIONS_LABEL,
    NO_RESULTS,
    GUIDE_TIMEOUT_MS,
    INITIAL_GUIDE_TIMEOUT_MS,
    QUICK_ACTIONS_GUIDE_TEXT,
    SEARCH_GUIDE_TEXT,
    LEARN_MORE_GUIDE_TEXT,

    components: {
        EcommQuickActions,
        EcommSettingsBlock,
    },

    props: {
        checkoutPageLink: {
            type: String,
            default: '',
        },
        secureServerString: {
            type: String,
            default: '',
        },
        appId: {
            type: String,
            default: '',
        },
        userId: {
            type: Number,
            default: 0,
        },
        hideConvrrtCheckout: {
            type: Boolean,
            default: true,
        },
        isKeapPayEnabled: {
            type: Boolean,
            default: false,
        },
        isUltimate: {
            type: Boolean,
            default: false,
        },
        isManageMerchantAccounts: {
            type: Boolean,
            default: false,
        },
        showPaypalVaultingModal: {
            type: Boolean,
            default: false,
        },
        paypalOnboardingUrl: {
            type: String,
            default: '',
        }
    },

    data() {
        return {
            defaultTheme: '',
            searchInput: '',
            ftu: {},
            displayQuickActionsGuide: false,
            displaySearchGuide: false,
            displayLearnMoreGuide: false,
            isVaultingActionInProgress: false,
        };
    },

    async mounted() {
        this.fetchDefaultTheme();

        this.ftu = await loadFtu(this.appId, this.userId);
        this.timeout(this.showQuickActionsGuide, this.$options.INITIAL_GUIDE_TIMEOUT_MS);
    },

    computed: {
        filteredSettingsBlocks() {
            const search = this.searchInput?.trim()?.toLowerCase();

            return this.filterBlocks(this.$options.ecommSetupEntries, search);
        },
    },

    methods: {
        fetchDefaultTheme() {
            const vm = this;

            axios
                .get('/app/styledTheme/getDefaultThemeUrl?shoppingCartType=CART')
                .then((response) => {
                    vm.defaultTheme = response.data.defaultShoppingCartThemeUrl;
                })
                .catch(() => {
                    vm.defaultTheme = '';
                });
        },

        showQuickActionsGuide() {
            if (!this.ftu[FTU_TYPES.ECOMMERCE_SETUP.QUICK_ACTIONS]) {
                this.displayQuickActionsGuide = true;
            } else {
                this.showSearchGuide();
            }
        },

        showSearchGuide() {
            if (!this.ftu[FTU_TYPES.ECOMMERCE_SETUP.SEARCH]) {
                this.displaySearchGuide = true;
            } else {
                this.showLearnMoreGuide();
            }
        },

        showLearnMoreGuide() {
            if (!this.ftu[FTU_TYPES.ECOMMERCE_SETUP.LEARN_MORE]) {
                this.displayLearnMoreGuide = true;
            }
        },

        dismissQuickActionsGuide() {
            if (this.displayQuickActionsGuide) {
                this.displayQuickActionsGuide = false;
                this.timeout(this.showSearchGuide);

                updateFtuItem(this.appId, this.userId, FTU_TYPES.ECOMMERCE_SETUP.QUICK_ACTIONS, true);
            }
        },

        dismissSearchGuide() {
            if (this.displaySearchGuide) {
                this.displaySearchGuide = false;
                this.timeout(this.showLearnMoreGuide);

                updateFtuItem(this.appId, this.userId, FTU_TYPES.ECOMMERCE_SETUP.SEARCH, true);
            }
        },

        dismissLearnMoreGuide() {
            if (this.displayLearnMoreGuide) {
                this.displayLearnMoreGuide = false;

                updateFtuItem(this.appId, this.userId, FTU_TYPES.ECOMMERCE_SETUP.LEARN_MORE, true);
            }
        },

        timeout(callback, timeoutMs = this.$options.GUIDE_TIMEOUT_MS) {
            setTimeout(() => {
                callback();
            }, timeoutMs);
        },

        filterBlocks(blocks, search) {
            let filteredBlocks = blocks.map((block) => {
                const { columns } = block;
                let filteredColumns = columns.map((column) => {
                    const { cards } = column;
                    let filteredCards = cards.map((card) => {
                        const filteredEntries = card.entries.filter(({ title, description }) => {
                            if (!this.isManageMerchantAccounts && title === 'Payment processing') {
                                return false;
                            }

                            let searchMatch = true;

                            if (search) {
                                searchMatch = title.toLowerCase().includes(search)
                                        || description.toLowerCase().includes(search);
                            }

                            if (this.hideConvrrtCheckout && title === 'Checkout Pages') {
                                searchMatch = false;
                            }

                            if (!this.isUltimate && (title === 'Payment dashboard' || title === 'Deposit dashboard')) {
                                searchMatch = this.isKeapPayEnabled;
                            }

                            return searchMatch;
                        });

                        if (filteredEntries.length > 0) {
                            return {
                                id: card.id,
                                footer: card.footer,
                                link: card.link,
                                entries: filteredEntries,
                            };
                        }

                        return false;
                    });

                    filteredCards = filteredCards.filter((card) => {
                        return card;
                    });

                    if (filteredCards.length > 0) {
                        return {
                            id: column.id,
                            cards: filteredCards,
                        };
                    }

                    return false;
                });

                filteredColumns = filteredColumns.filter((column) => {
                    return column;
                });

                if (filteredColumns.length > 0) {
                    return {
                        title: block.title,
                        columns: filteredColumns,
                    };
                }

                return false;
            });

            filteredBlocks = filteredBlocks.filter((block) => {
                return block;
            });

            return filteredBlocks;
        },
        async disconnectPaypal() {
            this.isVaultingActionInProgress = true;

            try {
                const response = await axios.post('/app/paymentOptions/disconnectPayPalCommerceAccount');
                const url = new URL(window.location);

                url.searchParams.set('messageType', response.data.messageType);
                url.searchParams.set('message', response.data.message);
                window.location = url.toString();
            } catch (error) {
                this.isVaultingActionInProgress = false;
                const url = new URL(window.location);

                url.searchParams.set('messageType', 'error');
                url.searchParams.set('message', 'Keap was unable to disconnect from your PayPal account.');
                window.location = url.toString();
            }
        },
        reconnectPaypal() {
            this.isVaultingActionInProgress = true;
        }
    },
};
</script>

<style lang="scss">

.input-field label {
    padding:            0 10px 0 0;
    --input-font-size:  14px;
}

</style>


<style lang="scss" scoped>

.ecomm-setup-page {
    min-height: 100%;
    margin-top: $spacing-400;
}

.actions {
    display:        flex;
    flex-wrap:      wrap;
    width:          100%;
    margin:         20px 0 24px 0;
    justify-items:  left;
}

.search-bar {
    flex: 72%;
}
.search-bar .input-field {
    width: 320px;
}

.setup-guide {
    flex: 13%;
}

.quick-actions {
    flex: 15%;
}

.paypal-vaulting-title {
    margin-bottom: $spacing-200;
}

.centered-spinner {
    margin: 0 auto;
}
</style>
